/* eslint-disable react-refresh/only-export-components -- pure operator table + logic helpers
   + a JSX value-input renderer shared by ColumnHeaderFilter.tsx; this module is not itself a
   component, so fast-refresh granularity doesn't apply. */
import { Input, Field } from '@fluentui/react-components';
import { EntityColumn } from './types';

// Shared Dataverse-style filter-operator logic — copied verbatim from ptc-core CRM
// (web/src/components/columnFilterOps.tsx). The OPERATORS table + currentOp/applyOperatorChange/
// applyFilterValue/renderValueInput/DATA_OPS are the single source of truth for the per-column
// header filter popover (ColumnHeaderFilter). Never duplicate this operator logic.
//
// Fluent v9 gotcha: controlled Dropdowns are wrapped in <Field> for their accessible name —
// NEVER aria-label on them (known render-loop hang that also wedges vitest).

export type FilterType = NonNullable<EntityColumn<unknown>['filterType']>;

export interface OperatorDef {
  op: string;
  label: string;
}

/** Ops that take no value: the key is emitted with an empty value and the input is disabled. */
export const DATA_OPS = new Set(['containsData', 'noData']);

/**
 * Sentinel entry for a picklist/lookup `in`/`notIn` value list meaning "has no value" (fleet
 * filter contract v3 §16.8 / case C86, Christian's ruling 2026-11-02 -- Client Outreach is the
 * reference implementation core lifts verbatim). Deliberately NOT a real-looking string: no
 * preparer name, status code or type code will ever collide with it. See picklistSelection.ts for
 * the read/write translation between this token and the lookup combobox's own UI model, and
 * applyClientFilters.ts's picklistPredicate for how it composes with `in`/`notIn` (additive-only:
 * a value list that never contains this token behaves exactly as it did before this token existed).
 */
export const NO_VALUE_TOKEN = '__TL_NO_VALUE__';

/** Boolean has no `filterOptions` of its own in the column catalog — it's inherently Yes/No.
 * Shared between ColumnHeaderFilter.tsx (renders it) and ActiveFiltersChips.tsx (labels a chip
 * built from it), so the two can never disagree on what "true"/"false" display as. */
export const BOOLEAN_LOOKUP_OPTIONS: { value: string; label: string }[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/** Operator menus per filterType, default first. */
export const OPERATORS: Record<FilterType, OperatorDef[]> = {
  text: [
    { op: 'contains', label: 'Contains' },
    { op: 'notContains', label: 'Does not contain' },
    { op: 'startsWith', label: 'Starts with' },
    { op: 'endsWith', label: 'Ends with' },
    { op: 'eq', label: 'Equals' },
    { op: 'ne', label: 'Does not equal' },
    { op: 'containsData', label: 'Contains data' },
    { op: 'noData', label: 'No data' },
  ],
  // 'On' (eq) and 'On or before' (lte) were blocked (fleet filter contract §12.3 / Rule D3) until
  // BL-142 landed in THIS engine's eq/lte/gt -- otherwise "On or before the 14th" would have
  // excluded the whole 14th, one click from every user, with no symptom. BL-142 landed in
  // applyClientFilters.ts's datePredicate (whole-day eq/lte/gt for a bare date bound); these two
  // are exposed now as a direct consequence. Order follows §12.1's Dataverse menu (On, On or
  // after, On or before, Between); the preset rows (Today/This week/etc.) and the fleet
  // extensions below a separator are NOT yet built here -- tracked separately, not invented ahead
  // of the posted contract.
  'date-range': [
    { op: 'eq', label: 'On' },
    { op: 'gte', label: 'On or after' },
    { op: 'lte', label: 'On or before' },
    { op: 'between', label: 'Between' },
    { op: 'lt', label: 'Before' },
    { op: 'containsData', label: 'Contains data' },
    { op: 'noData', label: 'No data' },
  ],
  number: [
    { op: 'eq', label: 'Equals' },
    { op: 'ne', label: 'Not equal' },
    { op: 'between', label: 'Between' },
    { op: 'gt', label: 'Greater than' },
    { op: 'gte', label: 'Greater than or equal' },
    { op: 'lt', label: 'Less than' },
    { op: 'lte', label: 'Less than or equal' },
    { op: 'containsData', label: 'Contains data' },
    { op: 'noData', label: 'No data' },
  ],
  picklist: [
    { op: 'in', label: 'Any of' },
    { op: 'notIn', label: 'None of' },
    { op: 'containsData', label: 'Contains data' },
    { op: 'noData', label: 'No data' },
  ],
  // `eq` stays first so a FRESH column with nothing staged still defaults to a non-excluding,
  // nothing-selected state (C86: boolean is a lookup column too, rendered via the same searchable
  // multi-select as picklist — see ColumnHeaderFilter.tsx). `eq` itself is no longer reachable from
  // that UI but stays in the table so a legacy `filter.<col>.eq` (from an old saved view or
  // hand-edited URL) is still DETECTED by currentOp() and translated by picklistSelection.ts rather
  // than silently falling through to the default.
  boolean: [
    { op: 'eq', label: 'Is' },
    { op: 'in', label: 'Any of' },
    { op: 'notIn', label: 'None of' },
    { op: 'containsData', label: 'Contains data' },
    { op: 'noData', label: 'No data' },
  ],
};

/** `filter.<colKey>.` prefix for a column's keys in the flat filter record. */
export function filterPrefix(colKey: string): string {
  return `filter.${colKey}.`;
}

/**
 * Resolve the active operator for a column from the filter record: a present
 * `filter.<col>.<op>` key always wins; else the remembered UI choice (for a column with no
 * value yet); else the filterType's default (first in {@link OPERATORS}).
 */
export function currentOp<TItem>(
  col: EntityColumn<TItem>,
  value: Record<string, string>,
  rememberedChoice?: string
): string {
  const ops = OPERATORS[col.filterType!];
  const prefix = filterPrefix(col.key);
  for (const o of ops) {
    if (`${prefix}${o.op}` in value) return o.op;
  }
  if (rememberedChoice && ops.some((o) => o.op === rememberedChoice)) return rememberedChoice;
  return ops[0].op;
}

/** Delete every `filter.<col>.*` key from `next` (mutates) — one condition per column. */
export function clearColumnFilters(next: Record<string, string>, colKey: string): void {
  const prefix = filterPrefix(colKey);
  for (const k of Object.keys(next)) {
    if (k.startsWith(prefix)) delete next[k];
  }
}

/**
 * Compute the next filter record after switching a column's operator, and whether the
 * caller should emit it. Carries the value over to the new key (data ops carry nothing);
 * only emits when there was a prior key for this column OR the new op is a data op.
 */
export function applyOperatorChange<TItem>(
  col: EntityColumn<TItem>,
  oldOp: string,
  newOp: string,
  value: Record<string, string>
): { next: Record<string, string>; shouldEmit: boolean } {
  const prefix = filterPrefix(col.key);
  const hadKey = Object.keys(value).some((k) => k.startsWith(prefix));
  const carried = DATA_OPS.has(oldOp) ? '' : value[`${prefix}${oldOp}`] ?? '';

  const next = { ...value };
  clearColumnFilters(next, col.key);
  if (DATA_OPS.has(newOp)) next[`${prefix}${newOp}`] = '';
  else if (carried !== '') next[`${prefix}${newOp}`] = carried;

  return { next, shouldEmit: hadKey || DATA_OPS.has(newOp) };
}

/** Compute the next filter record after setting a column's value under a given operator. */
export function applyFilterValue<TItem>(
  col: EntityColumn<TItem>,
  op: string,
  val: string,
  value: Record<string, string>
): Record<string, string> {
  const next = { ...value };
  clearColumnFilters(next, col.key);
  if (val !== '') next[`${filterPrefix(col.key)}${op}`] = val;
  return next;
}

/**
 * Value control for the non-lookup filterTypes (text, date-range, number). picklist and boolean
 * are NOT handled here (C86) — see ColumnHeaderFilter.tsx, which renders SearchableMultiSelect for
 * those two directly, so this never receives `facetCounts` for anything it would use.
 */
export function renderValueInput<TItem>(
  col: EntityColumn<TItem>,
  op: string,
  value: string,
  onChange: (v: string) => void
) {
  const disabled = DATA_OPS.has(op);
  const shown = disabled ? '' : value;
  const label = { children: `${col.label} value`, className: 'sr-only' };

  switch (col.filterType) {
    case 'text':
      return (
        <Field label={label}>
          <Input size="small" disabled={disabled} value={shown} onChange={(_, d) => onChange(d.value)} />
        </Field>
      );
    case 'date-range':
      return (
        <Field label={label}>
          <Input
            size="small"
            disabled={disabled}
            placeholder={op === 'between' ? '2025-01-01..2025-12-31' : '2025-01-01'}
            value={shown}
            onChange={(_, d) => onChange(d.value)}
          />
        </Field>
      );
    case 'number':
      return (
        <Field label={label}>
          <Input
            size="small"
            type={op === 'between' ? 'text' : 'number'}
            disabled={disabled}
            placeholder={op === 'between' ? '1..100' : undefined}
            value={shown}
            onChange={(_, d) => onChange(d.value)}
          />
        </Field>
      );
    // picklist and boolean are NOT handled here (fleet filter contract v3 §16.8 / C86,
    // 2026-11-02): both now render via SearchableMultiSelect directly in ColumnHeaderFilter.tsx —
    // one searchable multi-select combobox with facet counts, chips, Select all/Clear, an
    // "Exclude these" toggle and a "(has no value)" row, never an operator dropdown over a value
    // picker. See picklistSelection.ts for the {op,value} <-> selection translation.
    default:
      return null;
  }
}
